import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, User } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import PageHero from "@/components/site/PageHero";
import Seo, { ORG_SCHEMA, breadcrumbSchema } from "@/components/site/Seo";
import { publicApi } from "@/lib/crm";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}

export default function Insights() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicApi.listInsights().then((data) => {
      setItems(Array.isArray(data) ? data : []);
    }).finally(() => setLoading(false));
  }, []);

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": "https://velor.ae/insights#collection",
    url: "https://velor.ae/insights",
    name: "Velor Insights",
    description: "Bi-weekly briefings from Velor Advisory on the UAE Golden Visa, business setup, relocation, tax, property and banking.",
    inLanguage: "en-GB",
    mainEntity: {
      "@type": "ItemList",
      itemListElement: items.map((a, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `https://velor.ae/insights/${a.slug}`,
        item: {
          "@type": "Article",
          headline: a.title,
          description: a.excerpt,
          image: a.cover_image_url || undefined,
          datePublished: a.published_at,
          author: { "@type": "Person", name: a.author_name },
          publisher: { "@id": "https://velor.ae/#organization" },
        },
      })),
    },
  };

  return (
    <div className="bg-white">
      <Seo
        title="Insights — UAE Advisory Briefings"
        description="Bi-weekly briefings from Velor Advisory on the UAE Golden Visa, business setup, relocation, tax, property and banking for expats."
        keywords="Velor Advisory insights, UAE briefings, Golden Visa articles, Dubai business setup guide, UAE relocation blog"
        focusKeyword="UAE advisory insights"
        path="/insights"
        schema={[
          ORG_SCHEMA,
          collectionSchema,
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Insights", path: "/insights" },
          ]),
        ]}
      />
      <PageHero
        overline="Insights & Briefings"
        title="A closer look"
        subtitle="The things worth knowing before you move, invest or build a business in the UAE."
        image="https://images.pexels.com/photos/19664340/pexels-photo-19664340.jpeg?auto=compress&cs=tinysrgb&w=1650&fm=webp"
        imageAlt="Dubai skyline at golden hour — Velor Advisory insights and briefings on UAE relocation, visas and business"
      />

      <section className="py-24 md:py-32" data-testid="insights-list">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          {loading ? (
            <div className="text-black/50">Loading…</div>
          ) : items.length === 0 ? (
            <Reveal>
              <div className="max-w-xl">
                <div className="text-[11px] tracking-overline uppercase text-black/45">Coming soon</div>
                <h2 className="font-serif text-4xl md:text-5xl mt-4">The first briefing is on its way.</h2>
                <p className="mt-6 text-black/65 font-light leading-relaxed">
                  We're preparing the inaugural edition. In the meantime,{" "}
                  <Link to="/contact" className="underline underline-offset-4">book a private consultation</Link> or explore our{" "}
                  <Link to="/golden-visa" className="underline underline-offset-4">Golden Visa</Link>,{" "}
                  <Link to="/business-setup" className="underline underline-offset-4">business setup</Link> and{" "}
                  <Link to="/relocation" className="underline underline-offset-4">relocation</Link> services.
                </p>
              </div>
            </Reveal>
          ) : (
            <>
              <Reveal>
                <div className="text-[11px] tracking-overline uppercase text-black/45">Latest briefings</div>
                <h2 className="font-serif text-4xl md:text-5xl mt-4 max-w-2xl">Insight from our advisors.</h2>
              </Reveal>

              <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
                {items.map((a, i) => (
                  <Reveal key={a.id || a.slug} delay={i * 60}>
                    <Link
                      to={`/insights/${a.slug}`}
                      data-testid={`insight-card-${a.slug}`}
                      className="group block"
                    >
                      <div className="aspect-[4/3] w-full overflow-hidden bg-black/5">
                        {a.cover_image_url && (
                          <img
                            src={a.cover_image_url}
                            alt={a.title}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-[1200ms]"
                          />
                        )}
                      </div>
                      {a.category && (
                        <div className="mt-6 text-[11px] tracking-overline uppercase text-black/45">{a.category}</div>
                      )}
                      <h3 className="mt-3 font-serif text-2xl md:text-3xl leading-tight group-hover:text-black/70 transition-colors">
                        {a.title}
                      </h3>
                      {a.excerpt && (
                        <p className="mt-4 text-black/65 font-light leading-relaxed">{a.excerpt}</p>
                      )}
                      <div className="mt-6 flex items-center gap-4 text-[11px] tracking-overline uppercase text-black/45">
                        <span className="inline-flex items-center gap-1.5"><User size={12}/> {a.author_name}</span>
                        {a.published_at && (
                          <span className="inline-flex items-center gap-1.5"><Calendar size={12}/> {formatDate(a.published_at)}</span>
                        )}
                        {a.reading_time && <span>{a.reading_time} min read</span>}
                      </div>
                      <div className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-overline uppercase group-hover:gap-3 transition-all">
                        Read briefing <ArrowRight size={14}/>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
